import { FC } from "react";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { Popover, PopoverContent, PopoverTrigger } from "@/lib/shadcn/ui/popover";

// The (i) affordance in the Performance card header (airbnb-review-performance-table
// FR-017, AS-14). A Popover — not a Tooltip — because the copy is multi-paragraph
// and must be keyboard-dismissible and selectable. Radix handles focus-in on open,
// Escape-to-close, and focus-return to the trigger; do NOT override those.
const CohortInfoPopover: FC = () => {
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    data-testid="cohort-info-trigger"
                    aria-label="How the Cohort Score is calculated"
                    className="text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-full"
                >
                    <InfoCircledIcon className="h-4 w-4" />
                </button>
            </PopoverTrigger>
            <PopoverContent
                data-testid="cohort-info-content"
                align="end"
                className="w-96 space-y-3 text-sm"
            >
                <p>
                    <strong>Cohort Score</strong> — the average rating of <em>only</em> the reviews received in this
                    period.
                </p>
                <p>
                    Airbnb publishes one lifetime average per listing, not a monthly one. We work the period&rsquo;s own
                    score out by comparing the start and end snapshots: if a listing went from 4.80 over 100 reviews to
                    4.76 over 110, then those 10 new reviews averaged <strong>4.36</strong> — even though the headline
                    score barely moved.
                </p>
                <p>
                    That&rsquo;s why this column is more sensitive than <em>Drift</em>: a mature listing with hundreds of
                    reviews can have a bad month that its lifetime average hides for the best part of a year.
                </p>
                <p>
                    <strong>Why some cells are blank</strong> — a cohort is hidden when the listing&rsquo;s rounding
                    makes the period&rsquo;s score unreliable. Airbnb publishes category ratings to 1 decimal and the
                    overall to 2, and that rounding gets amplified when it is spread over the new reviews in a period. So
                    a mature listing needs more new reviews than a fresh one before its cohort is trustworthy — and we
                    would rather show nothing than a number you might act on.
                </p>
            </PopoverContent>
        </Popover>
    );
};

export default CohortInfoPopover;
